import { useRef, useState } from 'react'

import KindDot from '@/components/sections/updates/KindDot'
import ReleaseNote from '@/components/sections/updates/ReleaseNote'
import { CHANGE_KINDS, releaseId } from '@/components/sections/updates/releases'
import ReportIssue from '@/components/sections/updates/ReportIssue'
import Container from '@/components/ui/Container'
import Icon from '@/components/ui/Icon'
import { useData } from '@/hooks/useLocale'
import { useScrollSpy } from '@/hooks/useScrollSpy'

const LABEL =
  'px-3 pb-2.5 text-[11px] font-semibold tracking-[.16em] text-subtle uppercase'

/**
 * The release notes (design: Updates): the releases, newest first, beside a
 * sidebar (sticky from 900px up) that jumps to a release and filters the
 * changes by kind, with a count for each. The release being read is
 * highlighted in the sidebar. With no releases yet, an empty state stands in.
 * A card at the end asks visitors to report a bug.
 */
export default function ReleaseNotesSection() {
  const { UPDATES } = useData()
  const { releases } = UPDATES
  const [kind, setKind] = useState('all')
  const list = useRef(null)
  const empty = releases.length === 0

  // Each release with only the chosen kind of change; releases left with
  // nothing to show drop out.
  const shown = releases
    .map((release, i) => ({
      release,
      latest: i === 0,
      kinds: CHANGE_KINDS.filter(
        (k) => release[k]?.length && (kind === 'all' || kind === k),
      ),
    }))
    .filter((entry) => entry.kinds.length)
  const count = (k) =>
    releases.reduce(
      (n, release) =>
        n +
        CHANGE_KINDS.filter((c) => k === 'all' || c === k).reduce(
          (m, c) => m + (release[c]?.length ?? 0),
          0,
        ),
      0,
    )

  // The release being read, highlighted in the sidebar.
  const active = useScrollSpy(list, 'article[id]', kind)

  return (
    <section className="flex-1 bg-white pt-14 pb-30">
      <Container
        className={`grid items-start gap-x-14 gap-y-8 ${empty ? '' : 'md:grid-cols-[240px_minmax(0,1fr)]'}`}
      >
        {!empty && (
          <nav
            aria-label={UPDATES.releasesLabel}
            className="flex [scrollbar-width:none] flex-col rounded-[20px] bg-[#f8f9fb] p-4 md:sticky md:top-25 md:max-h-[calc(100vh-120px)] md:overflow-y-auto md:rounded-none md:bg-transparent md:p-0"
          >
            <span className={LABEL}>{UPDATES.releasesLabel}</span>
            {shown.map(({ release }) => {
              const id = releaseId(release.version)
              const on = id === active
              return (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`flex items-center justify-between gap-2.5 rounded-[10px] px-3 py-2 hover:bg-surface ${on ? 'bg-surface' : ''}`}
                >
                  <span
                    dir="ltr"
                    className={`text-[15px] font-semibold ${on ? 'text-brand' : 'text-ink'}`}
                  >
                    {release.version}
                  </span>
                  <span className="text-xs font-medium text-subtle">
                    {release.date}
                  </span>
                </a>
              )
            })}
            <div className="mx-3 my-3.5 h-px bg-line" />
            <span className={LABEL}>{UPDATES.showLabel}</span>
            <div className="flex flex-col gap-0.5">
              {['all', ...CHANGE_KINDS].map((k) => {
                const on = k === kind
                return (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setKind(k)}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-[10px] px-3 py-2 text-start text-sm hover:bg-surface ${on ? 'bg-surface font-semibold text-ink' : 'font-medium text-muted'}`}
                  >
                    <KindDot kind={k} />
                    {UPDATES.kinds[k]}
                    <span className="ms-auto text-xs text-[#a3a9b5]">
                      {count(k)}
                    </span>
                  </button>
                )
              })}
            </div>
          </nav>
        )}

        <div
          ref={list}
          className={`flex min-w-0 flex-col ${empty ? '' : 'max-w-190'}`}
        >
          {shown.map(({ release, latest, kinds }) => (
            <ReleaseNote
              key={release.version}
              release={release}
              latest={latest}
              kinds={kinds}
            />
          ))}

          {!empty && shown.length === 0 && (
            <p className="pb-10 text-[15px] text-subtle">{UPDATES.none}</p>
          )}

          {empty && (
            <div className="mb-6 flex min-h-[min(560px,62vh)] flex-col items-center justify-center gap-4 rounded-[28px] bg-linear-to-b/srgb from-[#fafbfd] to-white px-8 py-18 text-center inset-ring inset-ring-line">
              {/* Two release cards, a clock badge on the front one. */}
              <div aria-hidden="true" className="relative mb-2 h-24 w-33">
                <span className="absolute top-4.5 left-2 h-16.5 w-26 -rotate-6 rounded-2xl bg-white shadow-[0_0_0_1px_#eef1f5]" />
                <span className="absolute top-1.5 left-5 flex h-18 w-26 flex-col gap-2 rounded-2xl bg-white p-3.5 shadow-[0_0_0_1px_#e3e7ee,0_18px_30px_-18px_rgba(20,30,50,.35)]">
                  <span className="h-3.5 w-8.5 rounded-[7px] bg-brand-tint" />
                  <span className="h-1.5 w-17.5 rounded-[3px] bg-line" />
                  <span className="h-1.5 w-13 rounded-[3px] bg-line" />
                </span>
                <span className="absolute -right-0.5 bottom-0 grid size-9 place-items-center rounded-full bg-brand text-white ring-4 ring-white">
                  <Icon name="clock" size={16} stroke={2.2} />
                </span>
              </div>
              <h2 className="text-2xl font-semibold tracking-[-0.02em] text-ink">
                {UPDATES.empty.title}
              </h2>
              <p className="max-w-110 text-base/[1.65] text-muted">
                {UPDATES.empty.text}
              </p>
            </div>
          )}

          <ReportIssue />
        </div>
      </Container>
    </section>
  )
}
